import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { loginRequest, msalConfig } from "../utils/msalConfig";

const RETURN_PATH_KEY = "alumni.auth.returnPath";

class AuthService {
  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
    this.initializationPromise = null;
    this.tokenPromise = null;
    this.hasRedirectResponse = false;
  }

  async initialize() {
    if (!this.initializationPromise) {
      this.initializationPromise = this.msalInstance.initialize().then(async () => {
        const redirectResponse = await this.msalInstance.handleRedirectPromise();
        if (redirectResponse?.account) {
          this.msalInstance.setActiveAccount(redirectResponse.account);
          this.hasRedirectResponse = true;
        }
        const accounts = this.msalInstance.getAllAccounts();
        if (!this.msalInstance.getActiveAccount() && accounts.length > 0) {
          this.msalInstance.setActiveAccount(accounts[0]);
        }
      });
    }
    return this.initializationPromise;
  }

  getAccount() {
    return this.msalInstance.getActiveAccount();
  }

  isAuthenticated() {
    return Boolean(this.getAccount());
  }

  async login() {
    await this.initialize();
    return this.msalInstance.loginRedirect({
      ...loginRequest,
      prompt: "select_account",
    });
  }

  async getAccessToken() {
    await this.initialize();
    const account = this.getAccount();
    if (!account) return null;

    if (!this.tokenPromise) {
      this.tokenPromise = this.msalInstance
        .acquireTokenSilent({ ...loginRequest, account })
        .then((response) => response.accessToken)
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) return null;
          throw error;
        })
        .finally(() => {
          this.tokenPromise = null;
        });
    }
    return this.tokenPromise;
  }

  async logout() {
    await this.initialize();
    const account = this.getAccount();
    sessionStorage.removeItem(RETURN_PATH_KEY);
    await this.msalInstance.logoutPopup({
      account,
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      mainWindowRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    });
  }

  getDisplayName() {
    const account = this.getAccount();
    return account?.name || account?.username || "";
  }

  getUsername() {
    return this.getAccount()?.username || "";
  }

  rememberReturnPath(path) {
    if (path && path !== "/login") sessionStorage.setItem(RETURN_PATH_KEY, path);
  }

  consumeReturnPath(fallback = "/dashboard") {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return path && path.startsWith("/") ? path : fallback;
  }

  consumePostLoginPath() {
    if (!this.hasRedirectResponse) return null;
    this.hasRedirectResponse = false;
    return this.consumeReturnPath();
  }
}

export default new AuthService();
